import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { PermissionsService } from '@zonar-ui/auth';
import {BehaviorSubject, combineLatest, forkJoin, from, iif, Observable, of, throwError} from 'rxjs';
import {catchError, delay, filter, map, retry, switchMap, take} from 'rxjs/operators';
import { State as UserState } from './modules/udl/state';
import {
    setAccount,
    setAvailableAccounts,
    setCompanyId,
    setDispatcherEntityId,
    setUserProfileEntityId
} from './modules/udl/state/user.actions';
import {AppRouteData} from '@app/app.resolver';
import {IUserProfile} from '@zonar-ui/auth/lib/models/user-profile.model';
import { Division } from '@zonar-ui/sidenav/lib/models/company.model';


export const PREV_COMPANY_SELECTION_KEY = 'prevCompanySelection';

export interface PrevCompanySelection {
    id: string;
    name: string;
    userId: string;
    profileId: string;
    selectionTime: Date;
    legacyAccountCodeSelection?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AppService {

    public isAppDataLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private http: HttpClient,
        private userStore: Store<UserState>,
        private permissionsService: PermissionsService) {
    }

    setCompany(companyId: string, userProfile?: IUserProfile) {
        this.dispatchCompanyId(companyId);
        //note: set company context will set the user profile in the ZonarOwnerInterceptor
        this.permissionsService.setCurrentCompanyContextById(companyId);
        //if zonar user use different call for account info

        return this.permissionsService.getIsZonarUser().pipe(
            switchMap((isZonarUser) => {
                if(isZonarUser) {
                    return this.getCompanyLegacyTypeDivisions(companyId).pipe(
                        map(divisions => [...new Set((divisions || []).map(div => div.legacyAccountCode))]),
                        map(accounts => accounts.map(acc => ({ value: acc }))));;
                } else {
                    return this.getCompanyAccountsById(companyId);
                }
            }),
            switchMap((accounts) => forkJoin([
                of(accounts),
                this.permissionsService.getUser().pipe(take(1), map(users => users))
            ])),
            switchMap(([accounts, user]) => {
                // retrieve company legacy accounts and user info
                const prevSelection = this.getPrevCompanySelection();
                let selectedAcct = accounts[0].value;
                // If prev selection is present override with it.
                if (prevSelection && prevSelection.id === companyId
                    && prevSelection.legacyAccountCodeSelection) {
                    selectedAcct = prevSelection.legacyAccountCodeSelection;
                }
                //this sets for reload
                this.setPrevCompanySelection({
                    id: companyId,
                    name: '',
                    userId: user.id,
                    profileId: user.id,
                    selectionTime: new Date(),
                    legacyAccountCodeSelection: selectedAcct
                });
                // dispatch legacy account codes and what can be selected
                this.dispatchActiveAcct(selectedAcct, accounts);
                return of(selectedAcct);
            }),
            switchMap((acc) => this.permissionsService.getIsZonarUser().pipe(take(1),map(isZonarUser => ({ acc, isZonarUser })))),
            switchMap(({ acc, isZonarUser }) => {
                // retrieving dispatcher info depending on user state
                if (isZonarUser) {
                    return this.getZonarDispatcher(acc);
                } else {
                    return this.getDispatcher(acc);
                }
            }),
            switchMap(d => {
                // sets dispatcher state in app
                this.dispatchDispatcherEntity(d);
                return of(null);
            }),
            switchMap(() => this.permissionsService.getCompanyMap().pipe(take(1), map(companies => {
                if(companies && companies[companyId]) {
                    this.permissionsService.setCurrentCompanyContext(companies[companyId]);
                    return companies[companyId];
                }
                return null;
            }))),
            // Due to race condition, for Zonar users companyMap$ is empty sometimes on permission service.
            // So, here we are checking if company is null then making company call and setting it to the current context.
            switchMap((company) => iif(() => !!company, of(company),
                /* eslint no-underscore-dangle: 0 */
                this.permissionsService.getCompanyMap().pipe(map((companies) => {
                    if (companies && companies[companyId]) {
                        this.permissionsService.setCurrentCompanyContext(companies[companyId]);
                        return companies[companyId];
                    }
                    return null;
                })))),
            catchError(err => {
                console.log('err', err); return of(err);
            })
        );
    }

    setZonarUserDispatcherOnAccountChange(acc: string){
        this.permissionsService.getIsZonarUser()
            .subscribe((isZonarUser) => {
                if(isZonarUser) {
                    this.getZonarDispatcher(acc).subscribe(d => {
                        this.dispatchDispatcherEntity(d);
                    });
                } else {
                    //for normal users
                    this.setUserDispatcher(acc).subscribe((d) => {
                        this.dispatchDispatcherEntity(d);
                    });
                }
            });
    }

    setUserDispatcher(acc: string): Observable<any>{
        return this.permissionsService.getUser().pipe(
            switchMap((user) => this.getDispatcher(acc, user.id))
        );
    }

    dispatchActiveAcct(activeAcc: string, availableAccs: {value: string}[]): void {
        this.userStore.dispatch(setAccount({ payload: activeAcc }));
        this.userStore.dispatch(setAvailableAccounts({ payload: availableAccs }));
    }

    dispatchDispatcherEntity(d: { id: any}) {
        let dispatcher = d;
        if (Array.isArray(d)) {
            dispatcher = d[0];
        }
        this.userStore.dispatch(setDispatcherEntityId({ payload: dispatcher?.id?.toString() }));
    }


    dispatchUserProfileId(id: string){
        this.userStore.dispatch(setUserProfileEntityId({payload: id}));
    }

    dispatchCompanyId(id: string) {
        this.userStore.dispatch(setCompanyId({payload: id}));
    }

    getGTCMapping(accountCode?): Observable<string[]> {
        const params = {accountCode, entityType: 'company'};
        return this.http.get<string[]>(`${environment.urls.externalApi}/gtcmappings`, {params});
    }

    getCompanyByAccount(accountCode?): Observable<any[]> {
        if (!accountCode) {
            return of(null);
        }
        const params = {legacyAccountCode: accountCode};
        return this.http.get<string[]>(`${environment.urls.externalApi}/companies`, {params});
    }

    getCompanyAccountsById(companyId: string): Observable<{value: string}[]> {
        return this.getDivisionsByPermissionService(companyId)
            .pipe(
                map(divisions => [...new Set((divisions || []).map(div => div.legacyAccountCode))]),
                map(accounts => accounts.map(acc => ({ value: acc }))));
    }

    getCompanyLegacyTypeDivisions(companyId: string): Observable<Division[]> {
        // this call is only used by a zonar user
        return this.http.get<Division[]>(`${environment.urls.externalApi}` +
        `/companies/${companyId}/divisions?type=LEGACY&status=ACTIVE&sort=legacyAccountCode:asc`);
    }

    getDivisionsByPermissionService(companyId: string): Observable<any[]> {
        // getCompanyDivisions when called by a zonar user returns null
        // need delay because of company switching
        return from([1]).pipe(
            take(1),
            delay(1000),
            switchMap(num => this.permissionsService.getCompanyDivisions()),
            filter((x) => !!x),
            map( (x) =>
                x[companyId]?.filter((div) => !!div.legacyAccountCode && div.type === 'LEGACY' &&
                    div.status === 'ACTIVE')));
    }

    getCompanyBasedOnLegacyAccount(accountCode: string): Observable<any[]> {
        return this.http.get<string[]>(`${environment.urls.externalApi}/companies`, {params: {legacyAccountCode: accountCode}});
    }

    getDispatcher(acc, userId?): Observable<any[]> {
        return this.permissionsService.getUser().pipe(
            switchMap((user) => this.getDispatcherByEmail(acc, user.email))
        );

        // todo: commenting out entity api dispatchers call until we need to switch to it
        // const url = `${environment.urls.externalApi}/dispatchers`;
        // // This is due to a bug on zonar owner id http interceptor
        // // Adding retry(1) sometimes this api is throwing 401 error due to zonar owner id undefined, it works after a retry.
        // // if no dispatcher from the entity api, find the legacy id via user email
        // return this.http.get<any[]>(url, { params: { userId } }).pipe(
        //     retry(5),
        //     switchMap((dispatchers) =>
        //         iif(
        //             () => dispatchers.length > 0,
        //             of(dispatchers),
        //             this.permissionsService.getUser().pipe(
        //                 switchMap((user) => this.getDispatcherByEmail(acc, user.email))
        //             )
        //         )
        // ));
    }

    getZonarDispatcher(accountCode): Observable<any> {
        const url = `${environment.urls.udlApi}/zonar-dispatcher/`;
        return this.http.get(url, { params: { accountCode } })
            .pipe(
            catchError((err) => this.zonarDispatcherError(err, accountCode))
            );
    }

    getDispatcherByEmail(accountCode: string, email: string): Observable<any> {
        const url = `${environment.urls.legacyEntityApi}/api/v1/dispatchers`;
        return this.http.get(url, {params: { account_code: accountCode, email}});
    }

    setPrevCompanySelection(company: PrevCompanySelection): void {
        localStorage.setItem(PREV_COMPANY_SELECTION_KEY, JSON.stringify(company));
    }

    updateAccountSelection(account: string): void {
        const prevSelection = this.getPrevCompanySelection();
        if(prevSelection) {
            prevSelection.legacyAccountCodeSelection = account;
            this.setPrevCompanySelection(prevSelection);
        }
    }

    getPrevCompanySelection(): PrevCompanySelection {
        let company: PrevCompanySelection = null;
        const prevSelection = localStorage.getItem(PREV_COMPANY_SELECTION_KEY);
        if (prevSelection) {
            try {
                company = JSON.parse(prevSelection) as PrevCompanySelection;
            } catch (error) {
                console.log(error);
            }
        }
        return company;
    }

    clearPrevCompanySelection(): void {
        localStorage.removeItem(PREV_COMPANY_SELECTION_KEY);
    }

    private zonarDispatcherError(err: any, account: string): Observable<AppRouteData> {
        // silently swallows error on zonar dispatcher
        console.log(err);
        if (err.status && err.status !== 404) {
            return throwError(new HttpErrorResponse(err));
        } else {
            console.log('Zonar dispatcher id is not set');
            return of({
                activeAccount: account
            });
        }
    }

}
